import { Link } from 'react-router-dom';
import { Users, Heart, CheckCircle, Scale, Globe, Handshake } from 'lucide-react';

function EqualOpportunity() {
  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex items-center justify-center mb-6">
            <Handshake className="w-20 h-20 text-gold" />
          </div>
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Diversity & Inclusion</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Equal Opportunity <span className="text-gold">Employer</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-4xl mx-auto">
            Williams Diversified LLC is committed to creating an inclusive workplace where everyone has equal opportunities to succeed.
          </p>
        </div>
      </section>

      {/* Values Overview Cards */}
      <section className="bg-black py-16 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Scale className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Fair Employment</h3>
              <p className="text-gray-400">Equal opportunity in hiring, promotion, and compensation</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Globe className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Diverse Workforce</h3>
              <p className="text-gray-400">Celebrating diversity and fostering inclusion</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Heart className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Respectful Culture</h3>
              <p className="text-gray-400">Zero tolerance for discrimination and harassment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-black py-16 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gray-300 space-y-12">
            
            {/* Section 1: Our Commitment */}
            <div className="pt-28">
              <div className="flex items-center space-x-3 mb-6">
                <Users className="w-8 h-8 text-gold" />
                <h2 className="text-3xl font-bold text-white font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  1. Our Commitment to Equal Opportunity
                </h2>
              </div>
              <div className="space-y-4 text-lg">
                <p>
                  Williams Diversified LLC is proud to be an Equal Opportunity Employer. We are committed to providing equal employment opportunities to all employees and applicants for employment without regard to race, color, religion, sex, national origin, age, disability, genetic information, sexual orientation, gender identity, veteran status, or any other characteristic protected by applicable federal, state, or local law.
                </p>
                <p>
                  This commitment applies to all terms and conditions of employment, including recruiting, hiring, placement, promotion, termination, layoff, recall, transfer, leaves of absence, compensation, training, and other employment practices.
                </p>
              </div>
            </div>

            {/* Section 2: Non-Discrimination Policy */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                2. Non-Discrimination Policy
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Williams Diversified LLC does not discriminate on the basis of:
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Race or ethnicity</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Color</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Religion or creed</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Sex or gender</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Sexual orientation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Gender identity or expression</span>
                    </li>
                  </ul>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>National origin or ancestry</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Age</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Disability (physical or mental)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Genetic information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Veteran or military status</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Any other protected status</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 3: Workplace Diversity */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                3. Celebrating Diversity
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We believe that diversity strengthens our organization and enriches our workplace. A diverse workforce brings together different perspectives, experiences, and ideas that drive innovation and help us better serve our clients.
                </p>
                <p>
                  We actively seek to build a team that reflects the communities we serve and create an inclusive environment where all employees feel valued, respected, and empowered to contribute their best work.
                </p>
              </div>
            </div>

            {/* Section 4: Reasonable Accommodations */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                4. Reasonable Accommodations
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Williams Diversified LLC is committed to providing reasonable accommodations to qualified individuals with disabilities and for religious beliefs, unless doing so would cause undue hardship to the company.
                </p>
                <p>
                  If you require a reasonable accommodation to perform the essential functions of your job or to participate in the application process, please contact us to discuss your needs. We will work with you to identify and implement appropriate accommodations.
                </p>
                <div className="bg-black border border-gold/30 rounded-lg p-6 mt-6">
                  <h3 className="text-xl font-bold text-white mb-3">Examples of Reasonable Accommodations:</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Modifications to work schedules or break times</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Adjustments to workstations or equipment</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Assistive technology or devices</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Modified job duties or responsibilities</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Religious observance accommodations</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 5: Harassment-Free Workplace */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                5. Harassment-Free Workplace
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We are committed to maintaining a workplace free from harassment, intimidation, and discrimination. We have zero tolerance for any form of harassment, including but not limited to:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Sexual harassment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Racial or ethnic harassment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Religious harassment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Harassment based on age, disability, or any other protected characteristic</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Retaliation against those who report harassment or discrimination</span>
                  </li>
                </ul>
                <p className="mt-4">
                  Any employee found to have engaged in harassment or discrimination will be subject to disciplinary action, up to and including termination of employment.
                </p>
              </div>
            </div>

            {/* Section 6: Veterans and Military */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                6. Commitment to Veterans and Military Personnel
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Williams Diversified LLC recognizes and values the skills, dedication, and experience that veterans and military personnel bring to our workforce. We actively encourage applications from veterans and members of the military community.
                </p>
                <p>
                  We comply with all applicable laws regarding veterans' employment rights, including the Uniformed Services Employment and Reemployment Rights Act (USERRA), which protects the job rights of individuals who voluntarily or involuntarily leave employment to serve in the military.
                </p>
              </div>
            </div>

            {/* Section 7: Reporting Concerns */}
            <div className="bg-black border border-gold/30 rounded-lg p-8">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                7. Reporting Discrimination or Harassment
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  If you believe you have been subjected to discrimination, harassment, or retaliation, or if you have witnessed such behavior, we encourage you to report it immediately. You may report concerns to:
                </p>
                <ul className="space-y-3 ml-6 mt-4">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Your immediate supervisor</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Human Resources</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Company management</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Our confidential reporting hotline</span>
                  </li>
                </ul>
                <p className="mt-4">
                  All complaints will be investigated promptly, thoroughly, and impartially. We prohibit retaliation against anyone who reports discrimination or harassment or participates in an investigation. Confidentiality will be maintained to the extent possible while conducting a thorough investigation.
                </p>
              </div>
            </div>

            {/* Section 8: Compliance */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                8. Legal Compliance
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Our equal opportunity policies comply with all applicable federal, state, and local employment laws, including but not limited to:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Title VII of the Civil Rights Act of 1964</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Americans with Disabilities Act (ADA)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Age Discrimination in Employment Act (ADEA)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Equal Pay Act</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Genetic Information Nondiscrimination Act (GINA)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Uniformed Services Employment and Reemployment Rights Act (USERRA)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>All applicable state and local anti-discrimination laws</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 9: Career Opportunities */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                9. Join Our Team
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We welcome qualified applicants from all backgrounds to apply for positions at Williams Diversified LLC. We evaluate all candidates based on their qualifications, skills, and ability to perform the essential functions of the position.
                </p>
                <p>
                  If you are interested in joining our diverse and talented team, please visit our <Link to="/careers" className="text-gold hover:underline">Careers page</Link> to view current openings and submit your application.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-black border border-gold/30 rounded-lg p-8 mt-12">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                10. Contact Information
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  If you have questions about our Equal Opportunity Employment policies or wish to report a concern, please contact us:
                </p>
                <div className="space-y-3 mt-6">
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Company:</div>
                    <div className="pt-28">Williams Diversified LLC</div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Email:</div>
                    <div className="pt-28">
                      <a href="mailto:inquire@williamsdiversified.com" className="hover:text-gold transition-colors">
                        inquire@williamsdiversified.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Phone:</div>
                    <div className="pt-28">
                      <a href="tel:9184933418" className="hover:text-gold transition-colors">
                        (918) 493-3418
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Location:</div>
                    <div className="pt-28">Tulsa, Oklahoma</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Ready to Join <span className="text-gold">Our Team?</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Explore career opportunities and become part of our diverse, inclusive workforce
          </p>
          <Link 
            to="/careers"
            className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
          >
            View Career Opportunities
          </Link>
        </div>
      </section>
    </div>
  );
}

export default EqualOpportunity;
